import { useEffect, useRef, useState, type ReactNode } from 'react';

import { uiFormat, uiText } from '../../i18n';
import type { CircleFormState } from './circle/CircleFieldInput';
import type { StudioForm } from './studio-api';
import { StudioIcon } from './StudioIcon';
import './studio-photo-tray.css';

export type PhotoSlot = StudioForm['fields'][number];
/** Один лоток для экранов с несколькими фотографиями; UUID и ready-состояние выдаёт API. */
export function StudioPhotoTray({
  state,
  slots,
  urls,
  minimum,
  title = 'Фото',
  inputRef,
  consent,
  description = 'Порядок поменяете прямо в раскладке',
  main,
  onMain,
  checks,
}: {
  state: CircleFormState;
  slots: PhotoSlot[];
  urls: Record<string, string>;
  minimum: number;
  title?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  consent?: ReactNode;
  description?: string;
  /** Ключ главного фото: на квадрате метка «Главное» (§3, «Видео товара»; «Фотосессия товара») */
  main?: string;
  /** Нажатие на квадрат выбирает главное фото (и его кадр) */
  onMain?(key: string): void;
  /** Проверка снимка экраном: checking — «Проверяем», bad — причина отказа на квадрате, ok — без пометки */
  checks?: Record<string, { state: 'checking' | 'ok' | 'bad'; reason?: string }>;
}) {
  const ownInput = useRef<HTMLInputElement>(null);
  const input = inputRef ?? ownInput;
  const [error, setError] = useState('');
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const consentHint = uiText('Сначала отметьте согласие ниже');
  // Подсказка о согласии исчезает сама, как только права подтверждены.
  const shownError = state.rights && error === consentHint ? '' : error;
  const occupied = slots.filter(slot => state.uploadIds[slot.key] || state.inputs[slot.key]);
  const free = slots.filter(slot => !state.uploadIds[slot.key] && !state.inputs[slot.key]);
  const accept = slots[0]?.upload?.accept ?? ['image/jpeg', 'image/png', 'image/webp'];
  const maximum = Math.min(...slots.map(slot => slot.upload?.max_bytes ?? 10 * 1024 * 1024));
  async function select(files: FileList | null) {
    if (!files || !free.length || state.locked || !state.rights) return;
    setError(files.length > free.length ? uiFormat('Можно до {0} фото', slots.length) : '');
    for (const [index, file] of Array.from(files).slice(0, free.length).entries()) {
      if (!mounted.current) return;
      if (!accept.includes(file.type) || file.size > maximum) {
        setError(uiFormat('Выберите подходящее фото до {0} МБ.', maximum / 1024 / 1024));
        break;
      }
      if (!(await state.upload(free[index].key, file))) break;
    }
  }
  const blocked = state.locked || !state.rights || !free.length;
  // Кнопки загрузки неактивны до согласия; молчаливый отказ выглядит как поломка.
  const explainBlocked = () => {
    if (!state.locked && !state.rights && free.length) setError(consentHint);
  };
  return (
    <fieldset className={`studio-photo-tray studio-native-upload${occupied.length ? ' has' : ''}`}>
      <legend>
        {uiText(title)} · {occupied.length} / {slots.length}
      </legend>
      <div className={`bcu${occupied.length ? ' has' : ''}`}>
        {!occupied.length ? (
          <div className="bcu-empty">
            <span className="ic">
              <StudioIcon kind="photo" />
            </span>
            <b>{uiFormat('Загрузите от {0} до {1} фото', minimum, slots.length)}</b>
            <p>{uiText(description)}</p>
          </div>
        ) : (
          <div className="studio-photo-tray-grid">
            {occupied.map((slot, index) => (
              <div
                key={slot.key}
                className={`${main === slot.key ? 'main' : ''}${checks?.[slot.key]?.state === 'bad' ? ' bad' : ''}`.trim() || undefined}
              >
                {urls[slot.key] ? (
                  onMain ? (
                    <button
                      type="button"
                      className="studio-photo-tray-pick"
                      aria-pressed={main === slot.key}
                      aria-label={uiFormat('Главное фото: {0}', index + 1)}
                      disabled={state.locked}
                      onClick={() => onMain(slot.key)}
                    >
                      <img src={urls[slot.key]} alt={uiFormat('Фото {0}', index + 1)} />
                    </button>
                  ) : (
                    <img src={urls[slot.key]} alt={uiFormat('Фото {0}', index + 1)} />
                  )
                ) : (
                  <span role="status">{uiText(state.inputs[slot.key] ? 'Загружаем превью…' : 'Проверяем фото…')}</span>
                )}
                {main === slot.key && <span className="studio-photo-tray-main">{uiText('Главное')}</span>}
                {checks?.[slot.key]?.state === 'checking' && (
                  <span className="studio-photo-tray-check" role="status">
                    {uiText('Проверяем')}
                  </span>
                )}
                {checks?.[slot.key]?.state === 'bad' && (
                  <span className="studio-photo-tray-check bad" role="alert">
                    {uiText(checks[slot.key].reason ?? 'Фото не подошло')}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={uiFormat('Убрать фото {0}', index + 1)}
                  disabled={state.locked}
                  onClick={() => state.clearUpload(slot.key)}
                >
                  ×
                </button>
                {!state.inputs[slot.key] && (
                  <button type="button" disabled={state.locked} onClick={() => void state.upload(slot.key, null)}>
                    {uiText('Проверить загрузку')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="bcu-acts">
          <label className="bcu-pl" onClick={explainBlocked}>
            {uiText(occupied.length ? 'Добавить' : 'Загрузить')}
            <input
              ref={input}
              className="studio-native-file-input"
              aria-label={uiText('Загрузить фото')}
              type="file"
              multiple
              accept={accept.join(',')}
              disabled={blocked}
              onChange={event => {
                void select(event.target.files);
                event.target.value = '';
              }}
            />
          </label>
          <label className="bcu-pl" onClick={explainBlocked}>
            {uiText('Снять')}
            <input
              className="studio-native-file-input"
              aria-label={uiText('Снять фото')}
              type="file"
              capture="environment"
              accept={accept.join(',')}
              disabled={blocked}
              onChange={event => {
                void select(event.target.files);
                event.target.value = '';
              }}
            />
          </label>
        </div>
        <small>
          {accept.map(mime => mime.split('/')[1].toUpperCase()).join(', ')} · {uiFormat('До {0} МБ', maximum / 1024 / 1024)}
        </small>
        {consent}
      </div>
      {shownError && <p role="alert">{shownError}</p>}
    </fieldset>
  );
}
